import React, { useState, useEffect } from 'react';
import { detectDevice, getAppStoreUrls, markAppPromotionShown, dismissAppPromotion } from '../../utils/deviceDetection';

const EasterAppPromoPopup = ({ onClose, isVisible }) => {
  const [timeLeft, setTimeLeft] = useState({});
  const [device] = useState(detectDevice());
  const [isProcessing, setIsProcessing] = useState(false);
  const appStoreUrls = getAppStoreUrls();

  // Easter deadline - you can adjust this date
  const easterDeadline = new Date('2024-04-07T23:59:59'); // Easter Sunday 2024

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = +easterDeadline - +new Date();
      let timeLeft = {};

      if (difference > 0) {
        timeLeft = {
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        };
      }

      return timeLeft;
    };

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    setTimeLeft(calculateTimeLeft());

    return () => clearInterval(timer);
  }, []);

  const handleDownload = (store) => {
    if (isProcessing) return;
    setIsProcessing(true);
    
    markAppPromotionShown();
    
    // Track the download action
    if (window.gtag && typeof window.gtag === 'function' && window.ga4_loaded) {
      window.gtag('event', 'app_download_click', {
        event_category: 'Easter Promotion',
        event_label: store === 'apple' ? 'App Store' : 'Google Play',
        device_type: device.deviceType,
        os: device.os
      });
    }
    
    // Log to console for monitoring
    console.log('Easter Popup: Download clicked', {
      store: store === 'apple' ? 'App Store' : 'Google Play',
      device: device.deviceType,
      os: device.os,
      timestamp: new Date().toISOString()
    });

    // Open the appropriate store
    window.open(store === 'apple' ? appStoreUrls.apple : appStoreUrls.google, '_blank');
    
    // Close popup after download action
    setTimeout(() => {
      onClose();
    }, 500);
  };

  const handleDismiss = () => {
    if (isProcessing) return;
    setIsProcessing(true);
    
    markAppPromotionShown();
    
    // Track the no thanks action
    if (window.gtag && typeof window.gtag === 'function' && window.ga4_loaded) {
      window.gtag('event', 'app_promotion_dismiss', {
        event_category: 'Easter Promotion',
        event_label: 'No Thanks',
        device_type: device.deviceType,
        os: device.os,
        dismiss_duration: '24_hours'
      });
    }
    
    // Log to console for monitoring
    console.log('Easter Popup: No Thanks clicked', {
      device: device.deviceType,
      os: device.os,
      timestamp: new Date().toISOString()
    });
    
    dismissAppPromotion(24); // Dismiss for 24 hours
    onClose();
  };

  const handleClose = () => {
    if (isProcessing) return;
    setIsProcessing(true);
    
    markAppPromotionShown();
    
    // Track the close button action
    if (window.gtag && typeof window.gtag === 'function' && window.ga4_loaded) {
      window.gtag('event', 'app_promotion_close', {
        event_category: 'Easter Promotion',
        event_label: 'Close Button',
        device_type: device.deviceType,
        os: device.os,
        dismiss_duration: '24_hours'
      });
    }
    
    // Log to console for monitoring
    console.log('Easter Popup: Close button clicked', {
      device: device.deviceType,
      os: device.os,
      timestamp: new Date().toISOString()
    });
    
    dismissAppPromotion(24); // Dismiss for 24 hours
    onClose();
  };

  const handleLater = () => {
    if (isProcessing) return; // Prevent multiple clicks
    setIsProcessing(true);
    
    markAppPromotionShown();
    
    // Track the maybe later action - ONE TIME ONLY
    if (window.gtag && typeof window.gtag === 'function' && window.ga4_loaded) {
      window.gtag('event', 'app_promotion_later', {
        event_category: 'Easter Promotion',
        event_label: 'Maybe Later',
        device_type: device.deviceType,
        os: device.os,
        dismiss_duration: '4_hours'
      });
    }
    
    // Log to console for monitoring
    console.log('Easter Popup: Maybe Later clicked', {
      device: device.deviceType,
      os: device.os,
      timestamp: new Date().toISOString()
    });
    
    dismissAppPromotion(4); // Dismiss for 4 hours
    onClose();
  };

  if (!isVisible) return null;

  const isExpired = Object.keys(timeLeft).length === 0;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}>
      {/* Popup Container */}
      <div className="relative max-w-md w-full mx-auto transform transition-all duration-300 ease-out animate-scale-up">
        {/* Easter Decorative Background */}
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-yellow-100 via-orange-50 to-pink-100 opacity-90"></div>
        
        {/* Main Content */}
        <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-yellow-300">
          {/* Close Button */}
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>

          {/* Header with Easter Theme */}
          <div className="relative bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 p-6 text-center overflow-hidden">
            {/* Clean Professional Decorations */}
            <div className="absolute top-4 left-4 w-5 h-5 text-white/30 animate-pulse">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-full h-full">
                <circle cx="12" cy="12" r="10"/>
                <circle cx="12" cy="12" r="6"/>
                <circle cx="12" cy="12" r="2"/>
              </svg>
            </div>
            <div className="absolute top-4 right-4 w-4 h-4 text-white/30 animate-pulse" style={{ animationDelay: '1s' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-full h-full">
                <polygon points="12,2 22,8.5 22,15.5 12,22 2,15.5 2,8.5"/>
              </svg>
            </div>
            <div className="absolute bottom-4 left-4 w-4 h-4 text-white/30 animate-pulse" style={{ animationDelay: '2s' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-full h-full">
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 1v6m0 6v6"/>
                <path d="m21 12-6-3-6 3-6-3"/>
              </svg>
            </div>
            <div className="absolute bottom-4 right-4 w-5 h-5 text-white/30 animate-pulse" style={{ animationDelay: '0.5s' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-full h-full">
                <path d="m6 9 6 6 6-6"/>
              </svg>
            </div>
            {/*
            <h2 className="text-2xl font-bold text-white mb-2 drop-shadow-lg">
              <svg className="inline-block w-6 h-6 mr-2 mb-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="12,2 15.09,8.26 22,9 17,14 18.18,21 12,17.77 5.82,21 7,14 2,9 8.91,8.26"/>
              </svg>
              Easter Special!
              <svg className="inline-block w-6 h-6 ml-2 mb-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="12,2 15.09,8.26 22,9 17,14 18.18,21 12,17.77 5.82,21 7,14 2,9 8.91,8.26"/>
              </svg>
            </h2>
            */}
            <p className="text-white font-medium drop-shadow-md">
              Download Our Mobile App
            </p>
          </div>

          {/* Countdown Timer */}
          {!isExpired && (
            <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white py-3">
              <div className="text-center">
                <p className="text-sm font-medium mb-1 flex items-center justify-center">
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  Limited Time Offer Ends In:
                </p>
                <div className="flex justify-center space-x-2 text-lg font-bold">
                  <div className="bg-white bg-opacity-20 rounded px-2 py-1">
                    {String(timeLeft.days || 0).padStart(2, '0')}
                    <div className="text-xs font-normal">Days</div>
                  </div>
                  <div className="bg-white bg-opacity-20 rounded px-2 py-1">
                    {String(timeLeft.hours || 0).padStart(2, '0')}
                    <div className="text-xs font-normal">Hours</div>
                  </div>
                  <div className="bg-white bg-opacity-20 rounded px-2 py-1">
                    {String(timeLeft.minutes || 0).padStart(2, '0')}
                    <div className="text-xs font-normal">Min</div>
                  </div>
                  <div className="bg-white bg-opacity-20 rounded px-2 py-1">
                    {String(timeLeft.seconds || 0).padStart(2, '0')}
                    <div className="text-xs font-normal">Sec</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="mb-3 flex justify-center relative">
                {/* Animated Gift Icon Container */}
                <div className="relative w-20 h-20 flex items-center justify-center">
                  {/* Professional Gift Box SVG */}
                  <svg className="w-16 h-16 animate-pulse" xmlns="http://www.w3.org/2000/svg" shapeRendering="geometricPrecision" textRendering="geometricPrecision" imageRendering="optimizeQuality" fillRule="evenodd" clipRule="evenodd" viewBox="0 0 512 507.563">
                    <path fill="#012E67" fillRule="nonzero" d="M154.715 96.699h63.8c-37.318-14.394-66.884-50.077-36.499-82.661 31.844-34.15 83.918 1.114 105.188 30.344 11.086 15.238 20.278 33.323 28.598 52.317h5.981c8.32-18.994 17.513-37.079 28.598-52.317 21.27-29.23 73.345-64.494 105.19-30.344 30.385 32.584.819 68.267-36.5 82.661h67.394c7.036 0 13.425 2.87 18.045 7.49 4.619 4.619 7.49 11.009 7.49 18.044v36.602c0 6.969-2.869 13.341-7.492 17.987l-.084.085c-3.953 3.922-9.15 6.574-14.899 7.276v172.619c0 7.464-3.045 14.24-7.946 19.141-4.9 4.901-11.674 7.945-19.137 7.945H174.403c-7.462 0-14.239-3.044-19.139-7.945a14.25 14.25 0 01-1.007-1.118c-4.308-4.797-6.938-11.122-6.938-18.023V184.231c-5.934-.623-11.296-3.319-15.351-7.352l-.085-.087c-4.605-4.644-7.461-10.997-7.461-17.957v-36.602c0-6.983 2.877-13.365 7.519-18.016l-.028-.028c4.619-4.62 11.006-7.49 18.044-7.49h4.758zm188.234 0h42.617c86.331-34.012 60.395-96.227 9.844-67.407-11.483 6.547-21.187 18.838-29.877 31.084-9.072 12.783-17.062 26.285-22.584 36.323zm-90.93 0h42.617c-5.522-10.038-13.512-23.54-22.584-36.323-8.69-12.246-18.392-24.537-29.876-31.084-50.552-28.82-76.488 33.395 9.843 67.407z"/>
                    <path fill="#FECE00" d="M154.715 110.97h326.991l.146.002h4.613c6.195 0 11.262 5.07 11.262 11.261v36.602c0 6.189-5.07 11.261-11.262 11.261H149.957c-6.194 0-11.261-5.067-11.261-11.261v-36.602c0-6.195 5.067-11.261 11.261-11.261h4.612l.146-.002zm320.536 75.73v170.102c0 7.048-5.763 12.813-12.809 12.813H174.403c-7.046 0-12.811-5.765-12.811-12.813V186.7h313.659z"/>
                    <path fill="#012E67" fillRule="nonzero" d="M287.605 101.107h61.211c5.231 0 9.473 4.285 9.473 9.568v258.974c0 5.284-4.242 9.569-9.473 9.569h-61.211c-5.231 0-9.473-4.285-9.473-9.569V110.675c0-5.283 4.242-9.568 9.473-9.568zm51.738 19.137h-42.264v239.837h42.264V120.244z"/>
                    <path fill="#FF1D0D" d="M287.606 110.674h61.211v258.975h-61.211z"/>
                    <path fill="#fff" d="M318.21 127.313l6.585 11.218 12.24 3.08-8.171 10.014.98 13.121-11.636-5.03-11.633 5.03.98-13.123-8.171-10.012 12.242-3.081zM318.211 218.904l6.586 11.218 12.239 3.08-8.17 10.014.98 13.12-11.636-5.029-11.633 5.029.98-13.122-8.171-10.012 12.241-3.081zM318.211 300.452l6.586 11.219 12.239 3.08-8.17 10.013.98 13.121-11.636-5.029-11.633 5.029.98-13.123-8.171-10.011 12.241-3.082z"/>
                    <path fillRule="nonzero" d="M125.042 308.735h68.607c.389 0 .767.044 1.131.124 14.586 2.584 29.13 7.718 43.642 14.427 14.044 6.495 27.992 14.432 41.897 22.95h46.431l.674.044c8.466.561 15.738 3.256 20.904 7.247 3.748 2.895 6.444 6.499 7.787 10.526l.088.291c1.303 4.115 1.2 8.551-.604 13.027-2.005 4.977-6.141 9.962-12.83 14.472-9.914 7.114-20.264 10.464-31.461 11.74-10.829 1.231-22.247.495-34.843-.609l-.116-.011c-4.827-.119-7.334 2.637-7.345 5.457-.036 9.522 22.063 4.893 26.627 4.888 14.839-.012 28.314-1.385 39.815-5.568 10.99-3.999 20.264-10.648 27.323-21.3l7.627-17.388a5.14 5.14 0 012.492-2.588l77.406-37.505c.282-.137.572-.245.864-.327 14.054-4.47 26.878-3.159 36.663 1.697 6.047 3.004 10.954 7.364 14.319 12.573 3.408 5.28 5.23 11.428 5.055 17.93-.238 9.035-4.321 18.645-13.344 27.349a5.194 5.194 0 01-.647.529c-28.208 20.053-56.724 38.299-85.526 54.833a1006.473 1006.473 0 01-87.416 44.674c-44.35 26.253-88.677 25.347-132.977-.049l-62.243-29.358v2.452a5.19 5.19 0 01-5.189 5.189H76.546a5.19 5.19 0 01-5.19-5.189V305.204a5.192 5.192 0 015.19-5.189h43.307a5.19 5.19 0 015.189 5.189v3.531z"/>
                    <path fill="#F4C8A0" d="M125.052 313.918h68.597c28.404 4.979 56.808 20.026 85.213 37.5h47.888c23.556 1.387 35.891 20.895 13.01 36.224-18.256 13.072-38.176 12.333-62.85 10.17-17.017-.819-17.762 21.534 0 21.61 6.157.465 12.868-.945 18.721-.951 30.802-.028 56.172-5.782 71.697-29.559l7.805-17.796 77.407-37.502c38.737-12.463 66.262 23.314 37.728 50.844-56.065 39.861-113.573 72.661-172.378 99.153-42.715 25.388-85.429 24.526-128.144 0l-64.694-30.514V313.918z"/>
                    <path fill="#B29275" fillRule="nonzero" d="M311.613 357.864l1.415-.006c9.735-.049 25.251-.126 26.399 12.783l.004.081c.52 6.313-4.107 11.499-10.119 15.183-5.701 3.495-12.754 5.674-17.565 6.1l-.154.02c-1.079.086-2.029-.703-2.118-1.76l-.007-30.484c0-1.063.88-1.926 1.964-1.926l.181.009z"/>
                    <path fill="#FBF2E8" d="M311.432 359.781c8.821 0 24.986-.811 26.039 11.025.978 11.008-16.954 18.555-26.039 19.302v-30.327z"/>
                    <path fill="#F2EADF" d="M74.589 307.28h43.189v151.907H74.589z"/>
                    <circle fill="#4D4C4B" cx="99.34" cy="430.68" r="11.655"/>
                    <path fill="#2D1314" fillRule="nonzero" d="M2.075 290.677h73.958v185.112H0V290.677z"/>
                  </svg>
                  
                  {/* Animated Sparkles */}
                  <div className="absolute inset-0">
                    {/* Sparkle 1 */}
                    <div className="absolute top-1 left-2 w-2 h-2 animate-ping">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full text-yellow-400">
                        <path d="M12 2l3.09 6.26L22 9l-5 4.87L18.18 21 12 17.77 5.82 21 7 13.87 2 9l6.91-.74L12 2z" fill="currentColor"/>
                      </svg>
                    </div>
                    
                    {/* Sparkle 2 */}
                    <div className="absolute top-0 right-1 w-1.5 h-1.5 animate-ping" style={{ animationDelay: '0.5s' }}>
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full text-pink-400">
                        <path d="M12 2l3.09 6.26L22 9l-5 4.87L18.18 21 12 17.77 5.82 21 7 13.87 2 9l6.91-.74L12 2z" fill="currentColor"/>
                      </svg>
                    </div>
                    
                    {/* Sparkle 3 */}
                    <div className="absolute bottom-2 left-1 w-1.5 h-1.5 animate-ping" style={{ animationDelay: '1s' }}>
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full text-green-400">
                        <path d="M12 2l3.09 6.26L22 9l-5 4.87L18.18 21 12 17.77 5.82 21 7 13.87 2 9l6.91-.74L12 2z" fill="currentColor"/>
                      </svg>
                    </div>
                    
                    {/* Sparkle 4 */}
                    <div className="absolute bottom-1 right-2 w-2 h-2 animate-ping" style={{ animationDelay: '1.5s' }}>
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full text-orange-400">
                        <path d="M12 2l3.09 6.26L22 9l-5 4.87L18.18 21 12 17.77 5.82 21 7 13.87 2 9l6.91-.74L12 2z" fill="currentColor"/>
                      </svg>
                    </div>
                    
                    {/* Floating Confetti Particles */}
                    <div className="absolute top-3 left-3 w-1 h-1 bg-red-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    <div className="absolute top-4 right-3 w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.7s' }}></div>
                    <div className="absolute bottom-4 left-4 w-1 h-1 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '1.2s' }}></div>
                    <div className="absolute bottom-3 right-4 w-1 h-1 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '1.7s' }}></div>
                    
                    {/* Celebration Rings */}
                    <div className="absolute inset-0 border-2 border-yellow-300 rounded-full animate-ping opacity-30"></div>
                    <div className="absolute inset-2 border-2 border-pink-300 rounded-full animate-ping opacity-25" style={{ animationDelay: '0.3s' }}></div>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                Get Mystery Gifts & Prizes!
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Order our exclusive combos through the mobile app and receive mystery gifts and amazing prizes! 
                <span className="font-semibold text-orange-600"> Don't miss out on this limited-time offer!</span>
              </p>
            </div>

            {/* Benefits */}
            <div className="space-y-2 mb-6">
              <div className="flex items-center text-sm text-gray-700">
                <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Exclusive deals
              </div>
              <div className="flex items-center text-sm text-gray-700">
                <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Mystery gifts with order
              </div>
              <div className="flex items-center text-sm text-gray-700">
                <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Faster ordering & checkout
              </div>
              <div className="flex items-center text-sm text-gray-700">
                <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Push notifications for deals
              </div>
            </div>

            {/* Download Buttons */}
            <div className="space-y-3">
              {device.isIOS && (
                <button
                  onClick={() => handleDownload('apple')}
                  disabled={isProcessing}
                  className="w-full bg-black text-white py-3 px-4 rounded-xl font-medium flex items-center justify-center space-x-2 hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <span>Download on App Store</span>
                </button>
              )}
              
              {device.isAndroid && (
                <button
                  onClick={() => handleDownload('google')}
                  disabled={isProcessing}
                  className="w-full bg-green-600 text-white py-3 px-4 rounded-xl font-medium flex items-center justify-center space-x-2 hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <span>Get it on Google Play</span>
                </button>
              )}

              {/* Both platforms available */}
              {!device.isIOS && !device.isAndroid && (
                <>
                  <button
                    onClick={() => handleDownload('apple')}
                    disabled={isProcessing}
                    className="w-full bg-black text-white py-2 px-4 rounded-lg font-medium flex items-center justify-center space-x-2 hover:bg-gray-800 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    <span>App Store</span>
                  </button>
                  <button
                    onClick={() => handleDownload('google')}
                    disabled={isProcessing}
                    className="w-full bg-green-600 text-white py-2 px-4 rounded-lg font-medium flex items-center justify-center space-x-2 hover:bg-green-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                    </svg>
                    <span>Google Play</span>
                  </button>
                </>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-2 mt-4">
              <button
                onClick={handleLater}
                disabled={isProcessing}
                className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Maybe Later
              </button>
              <button
                onClick={handleDismiss}
                disabled={isProcessing}
                className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                No Thanks
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom styles for animations */}
      <style>{`
        @keyframes scale-up {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-scale-up {
          animation: scale-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default EasterAppPromoPopup;
